import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The CSG shapes with an empty shape, for the node's details. Godot warns on a shape whose brush
 * is empty and on each shape below it (`csg_shape.cpp:977-988`). A shape reports through
 * `useReportEmptyShape`, and with no provider a report does nothing.
 */
import { createContext, useContext, useMemo } from 'react';
import { parentPathOf } from '../../utils/nodePath.js';
import { createCountedReportsContext } from './countedReportsContext.js';
import { useNodePath } from './NodePathContext.js';
/** The path of each node with an empty shape. */
const emptyShapes = createCountedReportsContext('EmptyShapes');
export const EmptyShapesProvider = emptyShapes.Provider;
const ParentShapeContext = createContext(null);
ParentShapeContext.displayName = 'ParentShapeContext';
/**
 * Reports the enclosing node while its shape is empty, and returns whether it is. A shape is empty
 * when its own brush is, or when its direct CSG parent's shape is: `CSGBox3D > Node3D > CSGSphere3D`
 * is two roots, so only the parent's own path counts.
 */
export function useReportEmptyShape(hasOwnEmptyBrush) {
    const path = useNodePath();
    const parent = useContext(ParentShapeContext);
    const isParentEmpty = parent !== null && path !== null && parent.isEmpty && parent.path === parentPathOf(path);
    const isEmpty = hasOwnEmptyBrush || isParentEmpty;
    emptyShapes.useReportWhile(isEmpty ? path : null);
    return isEmpty;
}
/** Tells the shapes below whether the enclosing node's shape is empty. */
export function EmptyShapeBranch({ isEmpty, children }) {
    const path = useNodePath();
    const parent = useMemo(() => (path === null ? null : { path, isEmpty }), [path, isEmpty]);
    return _jsx(ParentShapeContext.Provider, { value: parent, children: children });
}
/** Whether the node at `nodePath` has an empty shape. */
export function useIsEmptyShape(nodePath) {
    return emptyShapes.useReported().has(nodePath);
}
//# sourceMappingURL=EmptyShapesContext.js.map